import React, { memo, useMemo } from 'react';

import Badge from '@/components/atomic/atoms/data-display/badge';
import SvgSprite from '@/components/atomic/atoms/data-display/icons/icon-sprite';
import FlexItem from '@/components/atomic/atoms/layout/flex-item';
import TopNavItemRenderer from '@/components/atomic/molecules/navigation/top-nav-items/item/renderer';
import { TopNavigationItemProps } from '@/components/atomic/molecules/navigation/top-nav-items/item/typings';
import ViewportRender from '@/components/atomic/molecules/utils/viewport/viewport-render';
import withExposer from '@/hocs/with-exposer';
import useIsIconValid from '@/hooks/use-is-icon-valid';
import useNormalisedSavedBuildsTotal from '@/hooks/use-normalised-saved-builds-total';
import { useTheme } from '@emotion/react';

const TopNavItemBuildsComponent: React.FC<
	Omit<TopNavigationItemProps, 'dropdownSegmentFirstIndex' | 'index'>
> = ({
	id,
	ariaLabel,
	label,
	link,
	fragmentVariationPath,
	dropdownSegment,
	icon,
	$variant,
}) => {
	const { size } = useTheme();

	const isIconValid = useIsIconValid(icon);

	const savedBuildsCount = useNormalisedSavedBuildsTotal();

	const canRenderBadge = savedBuildsCount && savedBuildsCount > 0;

	const mobileFormattedLabel = useMemo(() => {
		const str = [label];
		if (canRenderBadge) str.push(` (${savedBuildsCount})`);
		return str.join(' ');
	}, [canRenderBadge, label, savedBuildsCount]);

	return (
		<TopNavItemRenderer
			id={id}
			ariaLabel={ariaLabel}
			label={label}
			link={link}
			fragmentVariationPath={fragmentVariationPath}
			dropdownSegment={dropdownSegment}
			left={
				isIconValid ? (
					<FlexItem
						$alignItems='center'
						$justifyContent='center'
						$width={24}
						$position='relative'
						$breakpoints={{ [size.laptop]: { $width: 'auto' } }}
					>
						<ViewportRender
							node={null}
							$breakpoints={{
								[size.laptop]: {
									node: canRenderBadge ? (
										<Badge
											$top={26}
											$right={0}
											$transform='translate3d(30%, -50%, 0)'
										>
											{savedBuildsCount}
										</Badge>
									) : null,
								},
							}}
						/>
						<ViewportRender
							Component={SvgSprite}
							componentProps={{
								icon,
								$width: 18,
								$height: 18,
							}}
							$breakpoints={{
								[size.laptop]: {
									Component: SvgSprite,
									componentProps: {
										icon,
										$width: 20,
										$height: 20,
									},
								},
							}}
						/>
					</FlexItem>
				) : null
			}
			right={
				<ViewportRender
					node={
						mobileFormattedLabel ? (
							<FlexItem $alignItems='center'>
								<span>{mobileFormattedLabel}</span>
							</FlexItem>
						) : null
					}
					$breakpoints={{
						[size.laptop]: {
							node: label ? (
								<FlexItem $alignItems='center'>
									<span>{label}</span>
								</FlexItem>
							) : null,
						},
					}}
				/>
			}
			$variant={$variant}
		/>
	);
};

const TopNavItemBuilds = memo(
	withExposer(TopNavItemBuildsComponent, 'topNavItemBuilds'),
);

export default TopNavItemBuilds;
